'use client'

import { ContactsAddEditForm } from '@repo/contacts-organisations/ContactsAddEditForm'
import { DeleteIcon } from '@repo/ui/DeleteIcon'
import { EditIcon } from '@repo/ui/EditIcon'
import { LargeContactIcon } from '@repo/ui/LargeContactIcon'
import { LargeOrganisationIcon } from '@repo/ui/LargeOrganisationIcon'
import { OrganisationsAddEditForm } from '@repo/contacts-organisations/OrganisationsAddEditForm'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { formatDate } from '@repo/utils/time'
import { useEffect, useState } from 'react'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const ContactsOrganisationsPreview = ({
  itemId,
  mode = 'contacts',
  onEditModeChange = () => {},
  sx = {},
  handleDelete = () => {}
}) => {
  const { expertTypesById, expertAreasById, fetchOrganisationById, organisationsById, contactsById, marketingMailingCategories, marketingIntroductionSources } = useLookupsStore()

  const { allowedActions } = useMenuItemsStore()
  const item = mode === 'contacts' ? contactsById?.[itemId] : organisationsById?.[itemId]
  const [showEditForm, setShowEditForm] = useState(false)

  const allowEditContacts = allowedActions?.editContacts?.allowed || false
  const allowEditOrganisations = allowedActions?.editOrganisations?.allowed || false
  const allowEdit = mode === 'contacts' ? allowEditContacts : allowEditOrganisations

  useEffect(() => {
    const fetchOrganisation = async () => {
      if (item?.organisationId) {
        await fetchOrganisationById({ id: item.organisationId })
      }
    }

    if (mode === 'contacts' && item?.organisationId && !organisationsById?.[item.organisationId]) fetchOrganisation()
  }, [mode, item?.organisationId, fetchOrganisationById, organisationsById])

  useEffect(() => {
    setShowEditForm(false)
  }, [item])

  useEffect(() => {
    onEditModeChange(showEditForm)
  }, [showEditForm, onEditModeChange])

  const organisation = organisationsById?.[item?.organisationId] || null

  const data = mode === 'contacts' ? contactsById?.[item?.contactId] : organisation

  if (!data) return null

  const name = [data.title, data.forename, data.surname, data.name].filter(Boolean).join(' ')
  const address = [data.house, data.area, data.postalTown, data.county, data.postcode].filter(Boolean).join('\n')
  const expertType = expertTypesById && data?.expertTypeId ? expertTypesById[data.expertTypeId] : ''
  const expertArea = expertAreasById && data?.expertiseAreaId ? expertAreasById[data.expertiseAreaId] : ''

  const marketingMailingCategory = data?.marketingMailingCategoryId ?
    marketingMailingCategories.find(category => category.marketingMailingCategoryId === data.marketingMailingCategoryId)?.description
    : ''
  const introductionSource = data?.marketingIntroductionSourceId ?
    marketingIntroductionSources.find(source => source.marketingIntroductionSourceId === data.marketingIntroductionSourceId)?.description
    : ''
  const optedInToEmail = data?.hasOptedInToEmail != null ? (data.hasOptedInToEmail ? 'Yes' : 'No') : null
  const optedInToEmailDateTime = data?.optedInToEmailDateTime ? formatDate(data.optedInToEmailDateTime) : ''
  const optedInToEmailSourceUrl = data?.optedInToEmailSourceUrl || ''

  return (
    <MUIStack
      direction="column"
      sx={{ pl: '30px', pr: '0px', overflowX: 'hidden', ...sx }}
      data-testid="ContactsOrganisationsPreview"
    >
      <MUIStack direction="row" alignItems="center" gap={3} sx={{ p: 3, pr: 0, mb: 2, height: '72px' }}>
        {mode === 'contacts' ? (
          <LargeContactIcon sx={{ width: '40px', height: '40px' }} />
        ) : (
          <LargeOrganisationIcon sx={{ width: '40px', height: '40px' }} />
        )}

        <MUIStack direction="column" sx={{ flex: 1, width: 'calc(100% - 140px)' }}>
          <MUITypography variant="h5" sx={{ pointerEvents: 'none', mb: 0 }} noWrap>
            {name}
          </MUITypography>

          {mode === 'contacts' && (organisation?.name || expertType?.description) ? (
            <MUITypography variant="body1" sx={{ pointerEvents: 'none', mb: 0, color: 'text.secondary' }} noWrap>
              {[expertType?.description, organisation?.name].filter(Boolean).join(' | ')}
            </MUITypography>
          ) : null}

          {mode === 'organisations' && expertType?.description ? (
            <MUITypography variant="body1" sx={{ pointerEvents: 'none', mb: 0, color: 'text.secondary' }} noWrap>
              {[expertType?.description].filter(Boolean).join(' | ')}
            </MUITypography>
          ) : null}
        </MUIStack>

        <MUIStack direction="row" gap={2} sx={{ width: '80px' }}>
          {allowEdit && !showEditForm ? (
            <>
              <MUITooltip title="Delete" arrow placement="top">
                <MUIIconButton onClick={() => handleDelete(item, mode)}>
                  <DeleteIcon sx={{ width: '20px', height: '20px', opacity: showEditForm ? 0.5 : 1 }} />
                </MUIIconButton>
              </MUITooltip>

              <MUITooltip title="Edit" arrow placement="top">
                <MUIIconButton onClick={() => setShowEditForm(true)}>
                  <EditIcon sx={{ width: '20px', height: '20px', opacity: showEditForm ? 0.5 : 1 }} />
                </MUIIconButton>
              </MUITooltip>
            </>
          ) : null}
        </MUIStack>
      </MUIStack>

      {allowEdit && showEditForm && item && mode === 'contacts' ? (
        <ContactsAddEditForm
          contactId={item.contactId || null}
          onSuccess={() => setShowEditForm(false)}
          onCancel={() => setShowEditForm(false)}
        />
      ) : null}
      {allowEdit && showEditForm && item && mode === 'organisations' ? (
        <OrganisationsAddEditForm
          organisationId={item.organisationId || null}
          onSuccess={() => setShowEditForm(false)}
          onCancel={() => setShowEditForm(false)}
        />
      ) : null}

      {!showEditForm ? (
        <>
          <ContactsOrganisationsPreviewRow label="Name" value={name} />
          <ContactsOrganisationsPreviewRow
            label="ID"
            value={mode === 'contacts' ? String(data?.contactId) : String(data?.organisationId)}
          />
          <ContactsOrganisationsPreviewRow label="Expert type" value={expertType?.description} />
          <ContactsOrganisationsPreviewRow label="Expertise area" value={expertArea?.description} />
          <ContactsOrganisationsPreviewRow
            label="Organisation"
            value={mode === 'contacts' ? organisation?.name : null}
          />
          <ContactsOrganisationsPreviewRow label="Email" value={data?.email} />
          <ContactsOrganisationsPreviewRow label="Phone" value={data?.phoneNo} />
          <ContactsOrganisationsPreviewRow label="DX" value={organisation?.dxNo} />
          <ContactsOrganisationsPreviewRow label="Mobile" value={data?.mobileNo} />
          <ContactsOrganisationsPreviewRow label="Fax" value={data?.faxNo} />
          <ContactsOrganisationsPreviewRow label="URL" value={data?.websiteUrl} />
          <ContactsOrganisationsPreviewRow label="Address" value={address} multiline />
          <ContactsOrganisationsPreviewRow label="Notes" value={data?.notes} multiline showReadMoreOption />
          <ContactsOrganisationsPreviewRow label="Marketing mailing category" value={marketingMailingCategory} />
          <ContactsOrganisationsPreviewRow label="Introduction source" value={introductionSource} />
          <ContactsOrganisationsPreviewRow label="Opted in to email" value={optedInToEmail} />
          <ContactsOrganisationsPreviewRow label="Opted in to email date" value={optedInToEmailDateTime} />
          <ContactsOrganisationsPreviewRow label="Opted in to email source URL" value={optedInToEmailSourceUrl} />
        </>
      ) : null}
    </MUIStack>
  )
}

ContactsOrganisationsPreview.propTypes = {
  itemId: PropTypes.any,
  mode: PropTypes.oneOf(['contacts', 'organisations']),
  onEditModeChange: PropTypes.func,
  sx: PropTypes.object,
  handleDelete: PropTypes.func
}

const ContactsOrganisationsPreviewRow = ({ label, value, multiline = false, showReadMoreOption = false }) => {
  const [displayAllText, setDisplayAllText] = useState(!showReadMoreOption)
  const showMoreLimit = 180
  const needToShowReadMore = showReadMoreOption && value?.length > showMoreLimit

  if (!value) return null

  return (
    <MUIStack
      direction="row"
      sx={{
        borderTop: '1px solid #051C331F',
        padding: 3
      }}
    >
      <MUITypography
        variant="h6"
        sx={{ pointerEvents: 'none', mb: 0, minWidth: '260px', maxWidth: '260px', textTransform: 'uppercase', whiteSpace: 'pre-wrap' }}
        noWrap
      >
        {label}
      </MUITypography>

      <MUIStack direction="column" sx={{ flex: 1, alignItems: 'flex-start' }}>
        <MUITypography
          variant="body1"
          sx={{ pointerEvents: 'none', mb: 0, flex: 1, display: 'block', whiteSpace: 'pre-wrap' }}
          noWrap={!multiline}
        >
          {needToShowReadMore && !displayAllText ? `${value.slice(0, showMoreLimit)}...` : value}
        </MUITypography>

        {needToShowReadMore ? (
          <TextLinkButton
            onClick={() => setDisplayAllText(!displayAllText)}
            title={displayAllText ? 'Show less' : 'Show more'}
            sx={{ mt: 1, ml: 0, width: '80px', textAlign: 'left' }}
          />
        ) : null}
      </MUIStack>
    </MUIStack>
  )
}

ContactsOrganisationsPreviewRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  multiline: PropTypes.bool,
  showReadMoreOption: PropTypes.bool
}
